import form from "./Form.module.css";
import modal from "components/Modal/ModalContent.module.css";
import cn from "classnames";
import { Button } from "components/Button/Button";
import { Icon } from "gui/icon/icon";
import { Notification } from "components/Notification";

import { CronSchedule } from "./CronSchedule/CronSchedule";
import {
  TierAndCost,
  CostNotificationForQuery,
} from "./TierAndCost/TierAndCost";

import { useState } from "react";

import { toCronString } from "./cron/cronUtils";
import { currentHalfHour } from "./cron/optionsRate";
import type { ExecutionTiersData } from "shared/SelectPerformance/useGetPerformanceTiers";
import type { ActiveContext } from "shared/ContextSwitcher/store";
import type { FormState } from "./FormState";
import { toRateOption } from "./cron/optionsRate";
import { getMaxExecutionCountPerMonth } from "./cron/optionsRate/getMaxExecutionCountPerMonth";

interface Props {
  contextOwner: ActiveContext;
  performanceTiers: ExecutionTiersData[];
  initialState: undefined | FormState;
  save: (state: FormState) => void;
  stop?: () => void;
  close: () => void;
  somethingWentWrong: boolean;
}

export function ScheduleQueryExecutionModalContent(props: Props) {
  const [state, setState] = useState<FormState>(
    props.initialState ?? {
      cronString: daily,
      performanceTier: props.performanceTiers[0]?.publicPerformanceTierKey,
    }
  );

  const periodOption = toRateOption(state.cronString);
  const tier = props.performanceTiers.find(
    (tier) => tier.publicPerformanceTierKey === state.performanceTier
  );

  if (!tier) {
    return null;
  }

  if (periodOption === undefined) {
    return (
      <div className={modal.body}>
        <div className={modal.header}>
          <h3 className={modal.titleRow}>Schedule query refresh</h3>
        </div>

        <Notification
          level="warning"
          dismissable={false}
          title={<>Incompatible cron expression</>}
          description={
            <>
              <p>
                This query is scheduled to execute with the following cron
                expression: "<code>{state.cronString}</code>".
              </p>
              <p>
                It is using a format that is not supported in this UI, but you
                can delete the schedule and create a new one.
              </p>
            </>
          }
        />

        <div className={modal.buttons}>
          {props.stop && (
            <>
              <Button
                size="M"
                theme="primary"
                type="button"
                onClick={props.stop}
              >
                Delete schedule
              </Button>
            </>
          )}

          <Button size="M" theme="tertiary" type="button" onClick={props.close}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  const monthlyCredits =
    getMaxExecutionCountPerMonth(periodOption) * tier.credits;
  const planMonthlyCredits = props.contextOwner.serviceTier.includedCredits;

  return (
    <div className={modal.body}>
      <div className={modal.header}>
        <h3 className={modal.titleRow}>Schedule query refresh</h3>
        <p className={modal.description}>
          Set the time and day this query will run automatically.
        </p>
      </div>

      <form
        className={modal.subBody}
        onSubmit={(event) => {
          event.preventDefault();
          props.save(state);
        }}
      >
        <div className={cn(modal.content, form.fields)}>
          <CronSchedule state={state} setState={setState} />
          <hr />
          <TierAndCost
            contextOwner={props.contextOwner}
            performanceTiers={props.performanceTiers}
            state={state}
            setState={setState}
            CostNotification={CostNotificationForQuery}
            cost={{
              monthlyCredits,
              planMonthlyCredits,
            }}
          />
        </div>

        {props.somethingWentWrong && (
          <p className={form.errorMessage}>
            <Icon icon="warning-triangle" />
            Something went wrong, please try again.
          </p>
        )}

        <div className={modal.buttons}>
          {props.stop && (
            <>
              <Button
                size="M"
                theme="danger"
                type="button"
                onClick={props.stop}
              >
                Stop
              </Button>

              <hr className={modal.gapFillMiddle} />
            </>
          )}

          <Button size="M" theme="tertiary" type="button" onClick={props.close}>
            Cancel
          </Button>

          <Button size="M" theme="primary" type="submit">
            Save
          </Button>
        </div>
      </form>
    </div>
  );
}

const daily = toCronString(currentHalfHour());
